import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getPrendasInformation } from '../../services/prendasService';
import Swal from 'sweetalert2';

const BusquedaPrendas = ({ user, onLogout, onNavigate }) => {
  const [group, setGroup] = useState('');
  const [order, setOrder] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [hasBuscado, setHasBuscado] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // Formatear fecha
  // ─────────────────────────────────────────────────────────────
  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';

    try {
      if (fecha.includes('T')) {
        fecha = fecha.split('T')[0];
      }

      if (fecha.includes('-')) {
        const partes = fecha.split('-');

        if (
          partes.length === 3 &&
          partes[0].length === 4
        ) {
          return `${partes[2]}-${partes[1]}-${partes[0]}`;
        }
      }

      return fecha;
    } catch {
      return fecha;
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Buscar prenda
  // ─────────────────────────────────────────────────────────────
  const handleBuscar = async () => {
    if (!group || !order) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor, ingrese tanto el grupo como el orden.',
        confirmButtonColor: '#ef4444'
      });

      return;
    }

    try {
      setBuscando(true);
      setHasBuscado(true);

      const data = await getPrendasInformation(
        group,
        order,
        user.ce_ID ? user.ce_ID : 0
      );

      const prendaMapeada = {
        id: data.id || 0,

        group:
          data.group ||
          group,

        order:
          data.order ||
          order,

        titular:
          data.titular ||
          'N/A',

        ident:
          data.ident ||
          'N/A',

        sellerHouse:
          data.sellerHouse?.ceName ||
          data.sellerHouse ||
          'N/A',

        recordDate:
          formatearFecha(
            data.recordDate ||
            'N/A'
          ),

        // Fecha de creación de la prenda
        // Viene directamente del endpoint
        // Ejemplo: "23/07/2026 11:14"
        createDate:
          data.createDate ||
          'N/A',

        vehiculeTag:
          data.vehiculeTag ||
          'N/A',

        vehiculeSerial:
          data.vehiculeSerial ||
          'N/A',

        registerName:
          data.registerName ||
          'N/A',

        status:
          data.status ||
          'N/A',
      };

      setResultados([
        prendaMapeada
      ]);

    } catch (error) {
      console.error(
        'Error al buscar prenda:',
        error
      );

      Swal.fire({
        icon: 'error',
        title: 'Error en la búsqueda',
        text:
          error.message ||
          'No se pudo encontrar la prenda solicitada.',
        confirmButtonColor: '#ef4444'
      });

      setResultados([]);

    } finally {
      setBuscando(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Ver detalle
  // ─────────────────────────────────────────────────────────────
  const handleVerMas = (prenda) => {
    const prendaData = {
      group:
        prenda.group,

      order:
        prenda.order,

      titular:
        prenda.titular,

      ident:
        prenda.ident,

      sellerHouse:
        prenda.sellerHouse,

      recordDate:
        prenda.recordDate,

      vehiculeTag:
        prenda.vehiculeTag,

      // Se agrega createDate
      createDate:
        prenda.createDate,

      vehiculeSerial:
        prenda.vehiculeSerial,

      registerName:
        prenda.registerName,

      status:
        prenda.status,

      id:
        prenda.id,
    };

    localStorage.setItem(
      'prendaData',
      JSON.stringify({
        ...prendaData,
        fromDisponibles: false
      })
    );

    onNavigate(
      'visualizacionPrenda',
      {
        group: prenda.group,
        order: prenda.order
      }
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Color del estado
  // ─────────────────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (
    (status || '').toUpperCase()
    ) {
      case 'APROBADO':
      case 'FINALIZADO APTO':
        return 'text-green-600 bg-green-50';

      case 'RECHAZADO':
        return 'text-red-600 bg-red-50';

      case 'EN REVISION':
        return 'text-blue-600 bg-blue-50';

      case 'INCOMPLETO':
        return 'text-yellow-600 bg-yellow-50';

      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <MainLayout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
    >
      <div className="max-w-full mx-auto px-3 py-4">

        {/* Encabezado */}
        <div className="mb-8">

          <div className="flex items-center gap-3 mb-2">

            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

            <h2 className="text-2xl font-bold text-gradient">
              Búsqueda de Prendas
            </h2>

          </div>

          <p className="text-gray-500 text-sm ml-5">
            Busque prendas ingresando el grupo y orden
          </p>

        </div>

        {/* Formulario de búsqueda */}
        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8 relative overflow-hidden">

          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>

          <div className="relative">

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">

              {/* Grupo */}
              <div className="group">

                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                  Grupo
                </label>

                <div className="relative">

                  <input
                    type="text"
                    placeholder="Ingrese número de grupo"
                    value={group}
                    onChange={(e) =>
                      setGroup(
                        sanitizeInput(
                          e.target.value
                        )
                      )
                    }
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      handleBuscar()
                    }
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200"
                  />

                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">
                    tag
                  </span>

                </div>

              </div>

              {/* Orden */}
              <div className="group">

                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                  Orden
                </label>

                <div className="relative">

                  <input
                    type="text"
                    placeholder="Ingrese número de orden"
                    value={order}
                    onChange={(e) =>
                      setOrder(
                        sanitizeInput(
                          e.target.value
                        )
                      )
                    }
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      handleBuscar()
                    }
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all duration-200"
                  />

                  <span className="material-icons absolute right-3 top-3 text-gray-400 text-sm">
                    receipt_long
                  </span>

                </div>

              </div>

              {/* Botón buscar */}
              <div>

                <button
                  onClick={handleBuscar}
                  disabled={buscando}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all duration-200 text-sm font-semibold shadow-lg shadow-red-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >

                  {buscando ? (
                    <>
                      <span className="material-icons text-base animate-spin">
                        refresh
                      </span>

                      Buscando...
                    </>
                  ) : (
                    <>
                      <span className="material-icons text-base">
                        search
                      </span>

                      Buscar Prenda
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

        {/* Loader */}
        {buscando && (
          <div className="bg-white border border-gray-200 rounded-2xl p-12">

            <HondaLoader
              message="Buscando prenda..."
              variant="inline"
              size="large"
            />

          </div>
        )}

        {/* Resultados */}
        {!buscando &&
          hasBuscado && (

            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">

              {resultados.length === 0 ? (

                <div>

                  <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-3">

                    <p className="text-white text-sm font-bold">
                      Sin resultados
                    </p>

                  </div>

                  <div className="p-8 text-center text-gray-500">

                    <span className="material-icons text-4xl text-gray-300 mb-2">
                      search_off
                    </span>

                    <p>
                      No se encontraron prendas con los datos ingresados
                    </p>

                  </div>

                </div>

              ) : (

                <div className="overflow-x-auto">

                  <table className="min-w-full divide-y divide-gray-200">

                    <thead className="bg-gradient-to-r from-red-600 to-red-700">

                      <tr>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          Grupo
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          Orden
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          Titular
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          DNI/CUIT
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          F. Creación Prenda
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          Concesionario
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase">
                          Estado
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase">
                          Acciones
                        </th>

                      </tr>

                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">

                      {resultados.map(
                        (prenda) => (

                          <tr
                            key={prenda.id}
                            className="hover:bg-gray-50 transition-colors"
                          >

                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {prenda.group}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.order}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.titular}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.ident}
                            </td>

                            {/* F. Creación Prenda */}
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.createDate || 'N/A'}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-900">
                              {prenda.sellerHouse}
                            </td>

                            {/* Estado */}
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">

                              {prenda.status}

                            </td>

                            {/* Acciones */}
                            <td className="px-4 py-3 text-sm text-center">

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerMas(prenda);
                                }}
                                className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-1 rounded-full"
                                title="Ver detalle"
                              >

                                <span className="material-icons text-xl">
                                  visibility
                                </span>

                              </button>

                            </td>

                          </tr>

                        )
                      )}

                    </tbody>

                  </table>

                </div>

              )}

            </div>

          )}

      </div>

    </MainLayout>
  );
};

export default BusquedaPrendas;